import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericApiError } from '@/_shared/types/apiError';
import { API_LOGIN_WITH_SSO_CALL, KEY_GET_AUTH_SETTINGS } from '../constants';
import { MOCK_RESPONSE_LOGIN } from '../mocks';

type CallParamsGetAuthSettings = {
  idpAlias?: string;
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type Token = {
  tokenUrl: string;
  grant_type: string;
  client_id: string;
  headers: {
    Accept: string;
    'Content-Type': string;
  };
};

type LoginOrRegistration = {
  authorizeUrl: string;
  clientId: string;
  scope: string;
};

export type CallResponseGetLogin = {
  logoutUrl: string;
  idpProvider: string;
  registration: LoginOrRegistration;
  login: LoginOrRegistration;
  token: Token;
};

type CallErrorGetLogin = GenericApiError;

type CallSelectorsGetLogin = {
  logoutUrl: string;
  loginData: LoginOrRegistration;
  registrationData: LoginOrRegistration;
  tokenData: Token;
  idpProvider: string;
};

export const getAuthSettingsCall = createGeneralCall<
  CallParamsGetAuthSettings,
  CallResponseGetLogin,
  CallErrorGetLogin,
  CallSelectorsGetLogin
>({
  id: KEY_GET_AUTH_SETTINGS,
  url:
    ({ payload }) =>
    () => {
      const { idpAlias } = payload;
      return API_LOGIN_WITH_SSO_CALL(idpAlias);
    },
  successMock: IS_MOCK_ENABLED({ avoidMockOnDemoMode: true }) ? MOCK_RESPONSE_LOGIN : undefined,
  selectors: {
    idpProvider: ({ selectors }) =>
      createSelector(selectors.response, res => res?.idpProvider || ''),
    logoutUrl: ({ selectors }) => createSelector(selectors.response, res => res?.logoutUrl || ''),
    loginData: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.login || {
            authorizeUrl: '',
            clientId: '',
            scope: '',
          },
      ),
    registrationData: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.registration || {
            authorizeUrl: '',
            clientId: '',
            scope: '',
          },
      ),
    tokenData: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.token || {
            tokenUrl: '',
            grant_type: '',
            client_id: '',
            headers: {
              Accept: '',
              'Content-Type': '',
            },
          },
      ),
  },
});
